import { clearAllDrafts } from './draft';

/**
 * Who is signed in, as far as the browser knows.
 *
 * Two things are stored, and the second one is the interesting one. The token
 * is what the API needs; the surface is what the *router* needs, because a
 * route guard has to decide whether to render or redirect before any request
 * has been made. Asking the server "who am I" first would mean every guarded
 * route flashes a loading state on a hard refresh.
 *
 * This is a cache, not an authority. The token carries exactly one ability
 * (see Surface.php) and the API rejects it everywhere else, so the worst a
 * tampered `surface` value achieves is a console that renders its chrome and
 * then 401s on every call.
 */

export type Surface = 'ops' | 'merchant';

const TOKEN_KEY = 'blackarrow.token';
const SURFACE_KEY = 'blackarrow.surface';

/**
 * The admin's own token, stashed while an impersonation session borrows the
 * slot above. Sanctum hands out a plaintext token exactly once, so there is
 * no server call that could hand it back — this is the only copy, and the
 * reverse button's whole job is restoring it.
 */
const IMPERSONATOR_TOKEN_KEY = 'blackarrow.impersonator_token';

export interface Session {
    token: string;
    surface: Surface;
}

function isSurface(value: string | null): value is Surface {
    return value === 'ops' || value === 'merchant';
}

export function getSession(): Session | null {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const surface = localStorage.getItem(SURFACE_KEY);

        // Both or neither. A token with no surface is a half-written session
        // from an older build, and treating it as signed-in sends someone to a
        // console we cannot tell they belong in.
        if (token === null || token === '' || !isSurface(surface)) {
            return null;
        }

        return { token, surface };
    } catch {
        // Private mode with storage disabled. Signed out, every time.
        return null;
    }
}

export function setSession(session: Session): void {
    try {
        localStorage.setItem(TOKEN_KEY, session.token);
        localStorage.setItem(SURFACE_KEY, session.surface);
    } catch {
        /* private mode — the session just doesn't survive a refresh */
    }
}

export function clearSession(): void {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(SURFACE_KEY);

        // An ordinary sign-out while impersonating (rather than the reverse
        // button) would otherwise leave the admin's stashed token sitting in
        // storage indefinitely — dead weight nothing will ever read again.
        localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    } catch {
        /* nothing was stored to begin with */
    }

    // Unsent intake drafts belong to whoever was signed in. localStorage is
    // per-origin, not per-account, so on a shared back-office machine the next
    // person to sign in would otherwise inherit the last one's half-typed
    // customer name and address.
    clearAllDrafts();
}

/** The bearer token, for the API client. Null when signed out. */
export function getToken(): string | null {
    return getSession()?.token ?? null;
}

/**
 * The admin clicked Impersonate. Keeps the admin's own token to one side and
 * makes the impersonation token the active session, so every existing hook
 * that reads getToken() — no changes needed anywhere else — starts talking to
 * the API as the merchant.
 *
 * Silently does nothing to the stash if there is no admin session to keep;
 * that only happens if this is ever called from somewhere other than the ops
 * Merchants screen, which the caller controls.
 */
export function beginImpersonation(merchantToken: string): void {
    const admin = getSession();

    try {
        if (admin !== null) {
            localStorage.setItem(IMPERSONATOR_TOKEN_KEY, admin.token);
        }
    } catch {
        /* private mode — the reverse button just won't have anywhere to go back to */
    }

    setSession({ token: merchantToken, surface: 'merchant' });
}

/**
 * The reverse button. Restores the admin's stashed token rather than asking
 * the server for one — there is nothing to ask for; see IMPERSONATOR_TOKEN_KEY.
 */
export function endImpersonation(): void {
    let admin: string | null = null;

    try {
        admin = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    } catch {
        /* nothing to restore from */
    }

    if (admin === null) {
        clearSession();

        return;
    }

    setSession({ token: admin, surface: 'ops' });
}

/** Where a signed-in session belongs. */
export function homeFor(surface: Surface): '/ops' | '/merchant' {
    return surface === 'ops' ? '/ops' : '/merchant';
}

/** Where every surface signs in — one door for both since the account (not
 * the URL) decides which console it lands in. */
export function loginPathFor(): '/login' {
    return '/login';
}

/**
 * A session the server has stopped honouring.
 *
 * The route guards read localStorage, so they cannot tell a live token from a
 * revoked one — only a 401 can. Without this, a token that has expired, been
 * revoked, or outlived the database it was issued against renders the whole
 * console and then leaves every screen on a spinner, because a query that
 * errors never produces data and `data === undefined` reads as "still
 * loading". The session has to be cleared by the thing that learns it is dead.
 *
 * Returns where the browser should go, or null when there was nothing to end.
 * That null case is load-bearing: a failed sign-in also answers 401, and
 * bouncing the login screen off itself would make a wrong password look like
 * a broken app.
 */
export function expireSession(pathname: string): string | null {
    const session = getSession();

    if (session === null) {
        return null;
    }

    clearSession();

    const login = loginPathFor();

    // Same `?next=` contract the guards use, so a session that dies on a deep
    // link comes back to that link rather than to a dashboard.
    return pathname.startsWith('/') && !pathname.startsWith(login)
        ? `${login}?next=${encodeURIComponent(pathname)}`
        : login;
}
